import { makeAutoObservable, runInAction } from 'mobx';
import { notificationService, INotification } from '../../services/notification.service';
import { notificationSignalrService } from '../../services/notificationSignalr';
import { RootStore } from '../RootStore';

// Test için başlangıç bildirimleri (99 adet simüle)
const createTestNotifications = (): INotification[] =>
  Array.from({ length: 99 }, (_, i) => ({
    id: `test-${i + 1}`,
    title: `Bildirim ${i + 1}`,
    message: `Test bildirim mesajı ${i + 1}`,
    createdAt: new Date().toISOString(),
    isRead: false,
  }));

const NOTIFICATIONS_UPDATED_EVENT = 'NotificationsUpdated';
const BROADCAST_CHANNEL_NAME = 'kalden-notifications';

interface NotificationBroadcastMessage {
  type: 'markAsRead';
  notificationId: string;
}

class NotificationStore {
  rootStore: RootStore;
  notifications: INotification[] = [];
  isPopupOpen: boolean = false;
  isLoading: boolean = false;
  private notificationsUpdatedHandler?: (...args: unknown[]) => void;
  private broadcastChannel: BroadcastChannel | null = null;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    this.notifications = createTestNotifications();
    makeAutoObservable(this, {}, { autoBind: true });
  }

  /** Okunmamış bildirim sayısı - notifications'tan otomatik hesaplanır (sekme senkronizasyonu için) */
  get unreadCount(): number {
    return this.notifications.filter(n => !n.isRead).length;
  }

  /** Diğer sekmelere bildirim güncellemesi gönderir (BroadcastChannel - sunucu gerektirmez) */
  private notifyOtherTabs = (notificationId: string) => {
    try {
      if (typeof BroadcastChannel !== 'undefined') {
        const channel = this.broadcastChannel ?? new BroadcastChannel(BROADCAST_CHANNEL_NAME);
        this.broadcastChannel = channel;
        channel.postMessage({ type: 'markAsRead', notificationId } as NotificationBroadcastMessage);
      }
    } catch {
      // BroadcastChannel desteklenmiyorsa sessizce geç
    }
  };

  private handleBroadcastMessage = (event: MessageEvent<NotificationBroadcastMessage>) => {
    const msg = event.data;
    if (msg?.type === 'markAsRead' && msg.notificationId) {
      runInAction(() => {
        const notification = this.notifications.find(n => n.id === msg.notificationId);
        if (notification) {
          this.notifications = this.notifications.filter(n => n.id !== msg.notificationId);
        }
      });
    }
  };

  /** SignalR + BroadcastChannel bağlantısını başlatır - bir sekmede güncelleme olduğunda diğer sekmeler de güncellenir */
  startSignalR = () => {
    this.stopSignalR();
    // BroadcastChannel: Aynı tarayıcıdaki diğer sekmelere anında senkronizasyon
    if (typeof BroadcastChannel !== 'undefined') {
      this.broadcastChannel = new BroadcastChannel(BROADCAST_CHANNEL_NAME);
      this.broadcastChannel.onmessage = (e: MessageEvent) => this.handleBroadcastMessage(e);
    }
    const token =
      localStorage.getItem('jwtToken') || sessionStorage.getItem('jwtToken') || '';
    notificationSignalrService
      .connect(() => token)
      .then(() => {
        this.notificationsUpdatedHandler = (...args: unknown[]) => {
          const notificationId = args[0] != null ? String(args[0]) : undefined;
          if (notificationId) {
            runInAction(() => {
              const notification = this.notifications.find(n => n.id === notificationId);
              if (notification) {
                this.notifications = this.notifications.filter(n => n.id !== notificationId);
              }
            });
          } else {
            this.fetchNotifications();
          }
        };
        notificationSignalrService.on(
          NOTIFICATIONS_UPDATED_EVENT,
          this.notificationsUpdatedHandler
        );
      })
      .catch(err => console.error('Notification SignalR connect error', err));
  };

  stopSignalR = () => {
    if (this.notificationsUpdatedHandler) {
      notificationSignalrService.off(
        NOTIFICATIONS_UPDATED_EVENT,
        this.notificationsUpdatedHandler
      );
      this.notificationsUpdatedHandler = undefined;
    }
    if (this.broadcastChannel) {
      this.broadcastChannel.close();
      this.broadcastChannel = null;
    }
    notificationSignalrService.disconnect().catch(() => {});
  };

  fetchNotifications = async () => {
    this.isLoading = true;
    try {
      const response = await notificationService.getNotifications();
      if (!response.error && response.value && Array.isArray(response.value)) {
        runInAction(() => {
          this.notifications = response.value!.length > 0 ? response.value! : this.notifications;
        });
      }
    } catch {
      // Backend boş - test verileri kullanılıyor
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  markAsRead = async (id: string) => {
    try {
      await notificationService.markAsRead(id);
    } catch {
      // Backend boş - lokal olarak işle
    }
    runInAction(() => {
      const notification = this.notifications.find(n => n.id === id);
      if (notification) {
        this.notifications = this.notifications.filter(n => n.id !== id);
        this.notifyOtherTabs(id);
      }
    });
  };

  openPopup = () => {
    this.isPopupOpen = true;
    this.fetchNotifications();
  };

  closePopup = () => {
    this.isPopupOpen = false;
  };
}

export default NotificationStore;
